// src/utils/printDictamen.ts
import type { Dictamen } from '../views/Dictamenes/types';
import { renderPCRiesgo } from '../reports/PC-Riesgo';
import { renderPCHabitabilidad } from '../reports/PC-Habitabilidad';
import { renderPCPrevencion } from '../reports/PC-Prevencion';
export interface PrintOptions {
  autoClose?: boolean; // default: true
}

const normalize = (s?: string) =>
  (s ?? '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase();

const pad = (n: number) => String(n).padStart(2, '0');
const fechaCorta = (f?: string | Date) => {
  // Si viene como ISO (ej. "2026-05-01T00:00:00.000Z") tomar la parte de
  // fecha literal para no restar 6h por zona horaria (mostraba el día anterior).
  if (typeof f === 'string' && f.includes('T')) {
    const [y, m, dd] = f.split('T')[0].split('-');
    if (y && m && dd) return `${dd}/${m}/${y}`;
  }
  const d = f ? new Date(f) : new Date();
  // Usar getters UTC para evitar el desfase de zona en el caso restante.
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};
const fechaLugar = (d: Dictamen) =>
  `${normalize('TOTOLAC')}, ${normalize('TLAXCALA')}; ${fechaCorta(d.fechaSolicitud)}`;

const joinDir = (d: Dictamen) => {
  const partes = [d.direccion, d.colonia, 'TOTOLAC', 'TLAXCALA'].filter(Boolean);
  return partes.join(', ');
};

const contiene = (txt: string | undefined, ...palabras: string[]) => {
  const t = (txt ?? '').toLowerCase();
  return palabras.some(p => t.includes(p.toLowerCase()));
};

// Inferencias suaves con lo que sí existe en Dictamen
const inferExisteRiesgo = (d: Dictamen): boolean => {
  // Si el contenido menciona "NO EXISTE RIESGO", asumimos false.
  if (contiene(d.contenidoDictamen, 'no existe riesgo', 'sin riesgo')) return false;
  // Si menciona "EXISTE RIESGO" o "riesgo", asumimos true.
  if (contiene(d.contenidoDictamen, 'existe riesgo', 'riesgo')) return true;
  // Si el tipo incluye "RIESGO", asumimos true; si incluye "HABITA", false; si no, false por defecto.
  const t = normalize(d.tipo);
  if (t.includes('RIESGO')) return true;
  if (t.includes('HABITA')) return false;
  return false;
};

const inferCuentaMedidas = (d: Dictamen): boolean => {
  // Si en observaciones/ contenido dice "no cumple / no cuenta", entonces false
  if (contiene(d.observaciones, 'no cumple', 'no cuenta')) return false;
  if (contiene(d.contenidoDictamen, 'no cumple', 'no cuenta')) return false;
  // Si está "Entregado" podría implicar cumplimiento → true (heurística suave)
  if (d.estatusEntrega === 'Entregado') return true;
  // Si no hay indicios, true por defecto (puedes invertirlo si lo prefieres)
  return true;
};

const q = (v?: any) => (v ?? '').toString();

/**
 * Imprime un dictamen utilizando los templates .ts ya generados.
 * Decide template por d.tipo: incluye "HABITA" → Habitabilidad; "PREV"/"SEGURIDAD" → Prevención; en otro caso → Riesgo.
 */
export function imprimirDictamen(d: Dictamen, opts: PrintOptions = {}) {
  const tipo = normalize(d.tipo);
  let html: string;

  if (tipo.includes('HABITA')) {
    // ======= HABITABILIDAD =======
    const existe = inferExisteRiesgo(d); // true → hay riesgo
    html = renderPCHabitabilidad({
      dictamenFolio: q(d.folio || `DH/${d.id}`),
      area: 'COORDINACIÓN DE PROTECCIÓN CIVIL',
      fechaLugar: fechaLugar(d),

      destinatarioLinea: `C. ${q(d.nombre)}`,
      destinatarioPresente: 'PRESENTE',

      solicitante: q(d.nombre),
      predioDenominado: q(d.dictamenHabitabilidad?.predioDenominacion || 'Predio'),
      ubicacionDireccion: q(joinDir(d)),
      municipio: `Municipio de TOTOLAC; TLAXCALA.`,
      fechaSupervision: fechaCorta(d.fechaSolicitud),
      riesgoTexto: existe ? 'SÍ' : 'NO',
      funcionamientoTexto: existe ? 'NO SEGURO' : 'SEGURO Y BUEN',

      firma1Nombre: 'LIC. LUIS FERNANDO GERMÁN SÁNCHEZ',
      firma1Cargo: 'COORDINADOR DE PROTECCIÓN CIVIL',
      firma2Nombre: q(d.operador || 'VERIFICADOR'),
      firma2Cargo: 'VERIFICADOR',

      autoPrint: false
    });

  } else if (tipo.includes('PREV') || tipo.includes('SEGURIDAD')) {
    // ======= SEGURIDAD Y PREVENCIÓN =======
    const cuenta = inferCuentaMedidas(d);
    html = renderPCPrevencion({
      dictamenFolio: q(d.folio || `DSP/${d.id}`),
      area: 'COORDINACIÓN DE PROTECCIÓN CIVIL',
      fechaLugar: fechaLugar(d),

      destinatarioNombre: `C. ${q(d.nombre)}`,
      giroComercial: q(d.dictamenSeguridad?.giro || 'COMERCIO'),
      denominacion: `"${q(d.dictamenSeguridad?.denominacionNegocio || 'ESTABLECIMIENTO')}"`,
      presente: 'PRESENTE',

      titulo: 'DICTAMEN DE SEGURIDAD Y PREVENCIÓN',

      encabezadoVerificacion:
        'BASÁNDOSE EN LA VERIFICACIÓN OCULAR REALIZADA POR LA COORDINACIÓN DE PROTECCIÓN CIVIL MUNICIPAL',
      solicitante: q(d.nombre),
      ubicacionDireccion: q(joinDir(d)),
      fundamentoLegal:
        'CON FUNDAMENTO EN LOS ARTÍCULOS 36, 37 Y 38 DEL REGLAMENTO DE LA LEY DE PROTECCIÓN CIVIL',
      cuentaMedidasTexto: (d.dictamenSeguridad?.medidasMinimas || cuenta)
        ? 'SI CUENTA CON LAS MEDIDAS MÍNIMAS DE SEGURIDAD'
        : 'NO CUENTA CON LAS MEDIDAS MÍNIMAS DE SEGURIDAD',
      verificadorNombre: q(d.operador || 'MAURICIO CRUZ BERNAL'),
      determinacionTexto: (d.dictamenSeguridad?.medidasMinimas || cuenta) ? 'SÍ' : 'NO',

      firmaNombre: 'LUIS FERNANDO GERMÁN SÁNCHEZ',
      firmaCargo: 'COORDINADOR PROTECCIÓN CIVIL MUNICIPAL',
      vistoBuenoNombre: q(d.operador || ''), // opcional
      // firmaImgSrc: (no existe en Dictamen) → omitido
      autoPrint: false
    });

  } else {
    // ======= RIESGO (default) =======
    const existe = inferExisteRiesgo(d);
    html = renderPCRiesgo({
      dictamenFolio: q(d.folio || `DR/${d.id}`),
      area: 'COORDINACIÓN DE PROTECCIÓN CIVIL',
      fechaLugar: fechaLugar(d),

      destinatarioNombre: `C. ${q(d.nombre)}`,
      destinatarioCargo: 'PROPIETARIO',
      destinatarioUbicacion: `DE ${q(d.colonia || 'TOTOLAC')}, TLAXCALA`,
      destinatarioPresente: 'PRESENTE',

      titulo: 'DICTAMEN DE RIESGO',

      // Variables en negritas del documento
      lugarVerificacionTipo: q(d.dictamenRiesgo?.tipoActEven || 'establecimiento'),
      ubicacionReferencia: q(d.colonia || 'TOTOLAC'),
      domicilio: q(d.direccion),
      comunidad: q(d.colonia || 'TOTOLAC'),
      fechaVerificacion: fechaCorta(d.fechaSolicitud),
      hallazgos: q(d.contenidoDictamen || d.observaciones || '—'),
      fundamentoLegalRiesgo:
        'CON FUNDAMENTO EN LOS ARTÍCULOS 5 FRACCIÓN II Y III, Y 48 DEL REGLAMENTO DE LA LEY DE PROTECCIÓN CIVIL',
      existeRiesgoTexto: existe ? 'EXISTE RIESGO' : 'NO EXISTE RIESGO',
      recomendacion: q(d.dictamenRiesgo?.medidasMinimas || d.observaciones || 'DAR CUMPLIMIENTO A LAS MEDIDAS DE SEGURIDAD SEÑALADAS'),

      firma1Nombre: 'LIC. LUIS FERNANDO GERMÁN SÁNCHEZ',
      firma1Cargo: 'COORDINADOR DE PROTECCIÓN CIVIL',
      firma2Nombre: q(d.operador || 'VERIFICADOR'),
      firma2Cargo: 'VERIFICADOR'
    });
  }

  openPrintWindow(html, opts);
}

/**
 * Abre ventana, espera imágenes y hace print(); cierra si corresponde.
 */
function openPrintWindow(html: string, opts: PrintOptions) {
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return;

  w.document.open();
  w.document.write(html);
  w.document.close();

  const autoClose = opts.autoClose !== false;

  const finish = () => {
    try { w.focus(); } catch {}
    try {
      if ('onafterprint' in w) {
        w.onafterprint = () => { if (autoClose) w.close(); };
      }
      w.print();
    } catch {
      if (autoClose) w.close();
    }
  };

  const imgs = w.document.images;
  if (!imgs || imgs.length === 0) {
    finish();
    return;
  }

  let loaded = 0;
  const check = () => {
    loaded++;
    if (loaded >= imgs.length) finish();
  };

  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i];
    if (img.complete) {
      check();
    } else {
      img.onload = check;
      img.onerror = check;
    }
  }
}